/**
 * WordPress dependencies
 */
import Alpine from 'alpinejs';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import InlineCitationArtifact from './artifacts/inline_citation_artifact';
import FeaturedContentArtifact from './artifacts/featured_content_artifact';

//set the alpine prefix (default is x-)
Alpine.prefix('coai-x-')

//initialize alpinejs
Alpine.data('contentoracle_ai_chat', () => ({
	userMsg: "",
	apiBaseUrl: "",	//will be filled in by the block via php
	conversation: [],
	loading: false,
	error: "",
	chatNonce: "",	//will be filled in by the block via php
	stream_responses: true,
	scrollBlockIntoView: false,
	featured_content_border_classes: "",
	featured_content_button_classes: "",
	chat_log_id: null,
	is_in_widget_area: false,
	init() {
		console.log('init chat!!!');
		//load the rest url into the apiBaseUrl from the data-contentoracle_rest_url attribute
		this.apiBaseUrl = this.$el.getAttribute('data-contentoracle_rest_url');
		this.chatNonce = this.$el.getAttribute('data-contentoracle_chat_nonce');
		this.stream_responses = this.$el.getAttribute('data-contentoracle_stream_responses');
		this.scrollBlockIntoView = this.$el.getAttribute('data-contentoracle_scroll_block_into_view');
		this.featured_content_border_classes = this.$el.getAttribute('data-contentoracle_featured_content_border_classes').split(" ");
		this.featured_content_button_classes = this.$el.getAttribute('data-contentoracle_featured_content_button_classes').split(" ");
		this.chat_message_seeder_items = JSON.parse(this.$el.getAttribute('data-contentoracle_chat_message_seeder_items'));
		this.is_in_widget_area = this.$el.getAttribute('data-contentoracle_is_in_widget_area');

		//scroll to the top of the bottommost chat when the conversation updates
		this.$watch('conversation', () => {
			this.scrollToBottomMostChat(); // Call the function when conversation updates
		});

		//scroll to the bottom of the chat when the loading state updates
		this.$watch('loading', () => {
			if (this.loading) {
				this.scrollToBottom(); // Call the function when loading updates
			}
		});

		//scroll to the bottom of the chat when the error state updates
		this.$watch('error', () => {
			if (this.error) {
				this.scrollToBottom(); // Call the function when error updates
			}
		});

		//if we are in a widget area, attempt to load the conversation from local storage
		if (this.is_in_widget_area) {
			//get the conversation from local storage
			const conversationData = localStorage.getItem('contentoracle_ai_floating_chat_conversation');
			if (conversationData) {
				const parsedData = JSON.parse(conversationData);
				// Check if the conversation is less than a week old
				const oneWeekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
				if (parsedData.timestamp && parsedData.timestamp > oneWeekAgo) {
					this.conversation = parsedData.conversation;
				}
			}

			//get the chat log id from local storage
			const chatLogData = localStorage.getItem('contentoracle_ai_floating_chat_chat_log_id');
			if (chatLogData) {
				const parsedLogData = JSON.parse(chatLogData);
				// Check if the chat log is less than a week old
				const oneWeekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
				if (parsedLogData.timestamp && parsedLogData.timestamp > oneWeekAgo) {
					this.chat_log_id = parsedLogData.chat_log_id;
				}
			}
		}
		//otherwise, if we are not in the global chat widget area, then send a message from the url params
		//(if one is set)
		else {
			//preemptively add the search query to the conversation, if it exists
			const urlParams = new URLSearchParams(window.location.search);
			const searchQuery = urlParams.get('contentoracle_ai_search');
			if (searchQuery) {
				if (this.stream_responses) {
					this.sendStreamed(searchQuery, event)
				}
				else {
					this.send(searchQuery, event)
				}
			}
		}

		//scroll this block into view on page load if it is set to do so
		if (this.scrollBlockIntoView)
			this.scrollToBlock();
	},
	//sends a message using the input value
	async sendMessage(event) {
		event.preventDefault();	//prevent a page refresh due to form tag

		//ensure there is a message
		if (this.userMsg === '') {
			//NOTE: the validaity doesnt work, for some reason!
			this.$refs.chatInput.reportValidity();
			return
		}

		//ensure not already loading
		if (this.loading) {
			this.$refs.chatInput.setCustomValidity('Please wait, I am trying to come up with a response!');
			return
		}

		//ensure error is not set
		if (this.error) {
			return
		}

		//send the message
		if (this.stream_responses) {
			await this.sendStreamed(this.userMsg, event);
		}
		else {
			await this.send(this.userMsg, event);
		}

		//set the content of the input field to be empty
		this.$refs.chatInput.value = "";
	},
	//sends a message to the server and gets an ai response back
	async send(msg) {
		//set loading state, after a slight delay
		setTimeout(
			() => { this.loading = true; }, 1000
		);

		//prepare the request headers and body
		const url = this.apiBaseUrl + 'contentoracle-ai-chat/v1/chat';
		const headers = {
			'Content-Type': 'application/json',
			'X-WP-Nonce': this.chatNonce
		}
		const contextConversation = this.getConversationWithContext();
		const data = {
			message: msg,
			conversation: contextConversation.length <= 10 ? contextConversation : contextConversation.slice(contextConversation.length - 10),
			chat_log_id: this.chat_log_id,
		};
		//build the request
		const options = {
			method: 'POST',
			headers: headers,
			body: JSON.stringify(data),
		};

		//add the user's message to conversation 
		//this is done here so that the message is not already in the conversation when the message is sent to the
		//coai api, because if it is, the api will append it again, and the conversation will have two user messages in a row
		this.conversation.push({
			role: 'user',
			content: msg,
		});

		//if we are in a widget area, save the conversation to local storage
		if (this.is_in_widget_area) {
			localStorage.setItem('contentoracle_ai_floating_chat_conversation', JSON.stringify({
				conversation: this.conversation,
				timestamp: Date.now()
			}));
		}

		//send the request
		const request = await fetch(url, options)
		const json = await request.json();

		//handle the response
		if (json.error) {
			//this is an error that might be set in the wp api, because it is not a part of the response
			this.handleErrorResponse(json);
		}
		else {
			try {
				//add the response to the conversation
				//this is done here so that the message is not already in the conversation when the message is sent to the
				//coai api, because if it is, the api will append it again, and the conversation will have two user messages in a row
				const placeholder_response = {
					role: 'assistant',
					raw_content: json.response,
					content: "",
					content_used: [],
					content_supplied: json.content_supplied,
					action: json.action,
					engineered_prompt: json.engineered_prompt,
				}
				this.conversation.push(placeholder_response);

				//set the chat log id on this chat interface
				this.chat_log_id = json.chat_log_id;

				//parse the coai artifacts (updates the raw content with the parsed artifacts)
				const artifacts_parsed_content = this.renderArtifacts(this.conversation[this.conversation.length - 1]);

				//render and sanitize the markdown in the chat's raw content
				const md_rendered_content = DOMPurify.sanitize(
					marked.parse(
						artifacts_parsed_content
					)
				);

				//now, after all parses and transformations, set the chat content to the rendered chat
				this.conversation[this.conversation.length - 1].content = md_rendered_content;

				//if we are in a widget area, save the conversation to local storage
				if (this.is_in_widget_area) {
					localStorage.setItem('contentoracle_ai_floating_chat_conversation', JSON.stringify({
						conversation: this.conversation,
						timestamp: Date.now()
					}));
					if (this.chat_log_id) {
						localStorage.setItem('contentoracle_ai_floating_chat_chat_log_id', JSON.stringify({
							chat_log_id: this.chat_log_id,
							timestamp: Date.now()
						}));
					}
				}

				console.log("conversation", this.conversation, "chat_log_id", this.chat_log_id);
			}
			catch (e) {
				//don't use handleErrorResponse, because this is not the result of a malformed/bad response
				this.error = {
					error_code: "AI_CHAT_ERR",
					error_msg: "An error occurred while streaming in the response.",
				}
				console.error(e);
			}
		}

		//set loading state
		this.loading = false;

		//clear out input
		this.userMsg = '';
	},

	async sendStreamed(msg) {
		//set loading state, after a slight delay
		setTimeout(
			() => { this.loading = true; }, 1000
		);

		//add the user's message to conversation 
		//this is done here so that the message is not already in the conversation when the message is sent to the
		//coai api, because if it is, the api will append it again, and the conversation will have two user messages in a row
		this.conversation.push({
			role: 'user',
			content: msg,
		});

		//if we are in a widget area, save the conversation to local storage
		if (this.is_in_widget_area) {
			localStorage.setItem('contentoracle_ai_floating_chat_conversation', JSON.stringify({
				conversation: this.conversation,
				timestamp: Date.now()
			}));
		}

		//initialize the xhr request
		const xhr = new XMLHttpRequest();
		xhr.open(
			"POST",			//TODO: change later!
			this.apiBaseUrl + 'contentoracle-ai-chat/v1/chat/stream',//&message=' + msg,
			true
		);

		//set the headers
		xhr.setRequestHeader('Content-Type', 'application/json');
		xhr.setRequestHeader('X-WP-Nonce', this.chatNonce);

		//set streaming handler
		let finger = 0;
		let raw_response = "";	//store the raw response, so we don't try to double parse the markdown
		xhr.onprogress = function (event) {
			//set loading state
			this.loading = false;

			//get the response from the event
			const _response = event.target.response;

			//split on separator, the first "private use character" is the separator
			let responses = _response.split("\u{E000}").slice(finger);
			finger += responses.length - 1;

			//filter out empty strings
			responses = responses.filter((response) => response.length > 0);

			//iterate through the responses, parsing them
			responses.map((response) => {

				//push a chat bubble to the conversation for the ai response to fill, if one has not already been added
				if (this.conversation.length == 0 || this.conversation[this.conversation.length - 1].role != 'assistant') {
					this.conversation.push({
						role: 'assistant',
						content: "",
						content_used: [],
						content_supplied: [],
						action: null,
						engineered_prompt: null,
					});
				}

				//parse the response
				let parsed;
				let json_valid = true;
				try {
					parsed = JSON.parse(response);
				} catch (e) {
					//don't use handleErrorResponse, because this is not the result of a response
					console.error(e);
					//exit this call of the map callback
					json_valid = false;
				}

				//handle the response
				if (json_valid) {
					if (parsed?.error) {
						this.handleErrorResponse(parsed);
					}
					//else if it is the chat log id response
					else if (parsed?.chat_log_id) {
						//set the chat log id on this chat interface
						this.chat_log_id = parsed.chat_log_id;
					}
					//check if this is the action response
					else if (parsed?.action) {
						//handle the action
						if (!this.conversation?.action && this.conversation[this.conversation.length - 1].role == 'assistant') {
							this.conversation[this.conversation.length - 1].action = parsed.action;
						}
					}
					//else if it is the context supplied response
					else if (parsed?.content_supplied) {
						//set the context supplied on the ai's message
						if (!this.conversation?.action && this.conversation[this.conversation.length - 1].role == 'assistant') {
							this.conversation[this.conversation.length - 1].content_supplied = parsed.content_supplied;
						}
					}
					//else if it is the engineered input response
					else if (parsed?.engineered_prompt) {
						//set the engineered input on the ai's message
						if (!this.conversation?.action && this.conversation[this.conversation.length - 1].role == 'assistant') {
							this.conversation[this.conversation.length - 1].engineered_prompt = parsed.engineered_prompt;
						}
					}
					//otherwise, extract the generated message fragment
					else {
						//ensure the last message is not finished yet and it is an assistant message
						if (!this.conversation?.action && this.conversation[this.conversation.length - 1].role == 'assistant') {
							//append the fragment to the last message
							if (parsed?.generated?.message.length > 0) {
								raw_response += parsed?.generated?.message;
							}
						}

						//add the raw (unparsed) response to the last message
						this.conversation[this.conversation.length - 1].raw_content = raw_response;

						//parse the coai artifacts (updates the raw content with the parsed artifacts)
						const artifacts_parsed_content = this.renderArtifacts(this.conversation[this.conversation.length - 1]);

						//render and sanitize the markdown in the chat's raw content
						const md_rendered_content = DOMPurify.sanitize(
							marked.parse(
								artifacts_parsed_content
							)
						);

						//now, after all parses and transformations, set the chat content to the rendered chat
						this.conversation[this.conversation.length - 1].content = md_rendered_content;
					}
				}
			})
		}.bind(this);	//IMPORTANT: bind the this context to the alpine object, otherwise it will be the xhr object

		//set error handler
		xhr.onerror = function () {
			console.error(event);
		};

		//after the request is done
		xhr.onload = function () {


			//if we are in a widget area, save the conversation to local storage
			if (this.is_in_widget_area) {
				localStorage.setItem('contentoracle_ai_floating_chat_conversation', JSON.stringify({
					conversation: this.conversation,
					timestamp: Date.now()
				}));
				if (this.chat_log_id) {
					localStorage.setItem('contentoracle_ai_floating_chat_chat_log_id', JSON.stringify({
						chat_log_id: this.chat_log_id,
						timestamp: Date.now()
					}));
				}
			}

			console.log("conversation", this.conversation, "chat_log_id", this.chat_log_id);
		}.bind(this);	//IMPORTANT: bind the this context to the alpine object, otherwise it will be the xhr object

		//send the request with the body
		const contextConversation = this.getConversationWithContext();
		const data = {
			message: msg,
			conversation: contextConversation.length <= 10 ? contextConversation : contextConversation.slice(contextConversation.length - 10),
			chat_log_id: this.chat_log_id,
		};
		xhr.send(JSON.stringify(data));
	},

	//looks at the raw content of a chat, and replaces it with the raw content with artifacts parsed and rendered
	renderArtifacts(chat) {

		//gregex for all artifact tags
		const regex = /<coai-artifact[^>]*>(.*?)<\/coai-artifact>/gs;


		//get the raw content of the chat
		const raw_content = chat.raw_content;

		//render each artifact
		const artifacts_rendered = raw_content.replace(regex, (match, innerText) => {
			//parse the tag, and find it's type
			const parser = new DOMParser();
			const doc = parser.parseFromString(match, 'text/html');
			const artifact = doc.querySelector('coai-artifact');
			const artifact_type = artifact.getAttribute('artifact_type');

			//use the artifact parsers to parse each artifact based on it's type
			let rendered;
			switch (artifact_type) {
				case 'inline_citation':
					let inline_citation = new InlineCitationArtifact(artifact);
					rendered = inline_citation.render(chat.content_supplied, chat.content_used);	//NOTE: these are modified by reference
					return rendered.outerHTML;
				case 'featured_content':
					let featured_content = new FeaturedContentArtifact(artifact);
					rendered = featured_content.render(chat.content_supplied, this.featured_content_border_classes, this.featured_content_button_classes);	//NOTE: these are modified by reference
					return rendered.outerHTML;
				// default:
				// 	return innerText;
			}
		})

		return artifacts_rendered;
	},
	//scrolls to the bottom of the chat
	scrollToBottom(event) {
		const chatContainer = this.$refs.chatBody;
		chatContainer.scrollTop = chatContainer.scrollHeight;
	},
	//scrolls to the top of the bottommost assistant chat
	scrollToBottomMostChat(event) {
	},
	//performs all tasks that need to be performed when an error response is received
	handleErrorResponse(error) {
		this.error = error; //`Error ${error.error.error || error.error_code}: "${error.error_msg}".`;	//|| output hte unauthorized error properly
		console.error(`Error originates from ${error.error_source == "coai" ? "ContentOracle AI API" : "WordPress API"}.`, error.error);
	},

	//get the conversations, with the context prepended to the user message
	getConversationWithContext() {
		//make a copy of the conversation, to avoid state mutation
		const conversation = JSON.parse(JSON.stringify(this.conversation));
		let content_used = null;
		let content_used_str = null;

		//iterate through the conversation, changing every user message's content to the engineered input from the assistant message after it
		for (let i = conversation.length - 1; i >= 0; i--) {
			//if this is a user message
			if (conversation[i].role == 'user') {
				//if there is an assistant message after this one
				if (i + 1 < conversation.length && conversation[i + 1].role == 'assistant') {
					//set the user message content to the assistant message's engineered input
					if (conversation[i + 1].engineered_prompt) {
						conversation[i].content = conversation[i + 1].engineered_prompt
					}
				}
			}
		}

		//return the conversation
		return conversation;
	},

	//scroll the window to this block
	scrollToBlock(event) {
		//scroll the block into view
		this.$el.scrollIntoView({ behavior: "smooth" });
	},

	//use one of the chat message seeder items as the user's message
	useChatMessageSeederItem(msg) {
		//send the message (note, the calls below are async, KEEP THIS IN MIND)
		if (this.stream_responses) {
			this.sendStreamed(msg, {});
		}
		else {
			this.send(msg, {});
		}
	},

	//reset the chat state to defaults
	resetChat() {
		//reset all state variables to their defaults
		this.userMsg = "";
		this.conversation = [];
		this.loading = false;
		this.error = "";
		this.chat_log_id = null;

		//clear the global chat conversation, if this is a global chat widget area
		if (this.is_in_widget_area) {
			localStorage.removeItem('contentoracle_ai_floating_chat_conversation');
		}

		//clear the input field
		if (this.$refs.chatInput) {
			this.$refs.chatInput.value = "";
		}

		//focus back on the input field
		if (this.$refs.chatInput) {
			this.$refs.chatInput.focus();
		}
	}
})
)

Alpine.start();